import { ReactNode } from 'react'

import Tooltip from '@/components/Tooltip'
import { cn } from '@/lib/misc/Utils'

const MAX_SHOWN_COUNT = 9

interface NavBadgeProps {
  label: string
  count?: number
  overlay?: boolean
  tooltip?: ReactNode
  className?: string
  cy?: string
}

/**
 * A small amber marker that flags something waiting on the user. Without a
 * count it is a dot; `overlay` draws the count as a solid badge that can sit
 * on top of an icon.
 */
const NavBadge = ({
  label,
  count,
  overlay = false,
  tooltip,
  className,
  cy,
}: NavBadgeProps) => {
  const badge = (
    <span
      data-cy={cy}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full tabular-nums',
        !tooltip && 'pointer-events-none',
        !count && 'size-2 bg-status-warning ring-2 ring-surface',
        !!count &&
          overlay &&
          'h-4 min-w-4 bg-status-warning px-1 text-[10px] font-semibold leading-none text-neutral-100 ring-2 ring-surface',
        !!count &&
          !overlay &&
          'h-5 min-w-5 bg-status-warning px-1.5 text-[11px] font-bold leading-none text-neutral-100 shadow-[0_1px_2px_rgb(143_87_0/0.25)]',
        !tooltip && className
      )}
    >
      {!!count && (
        <span aria-hidden>
          {count > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : count}
        </span>
      )}
      <span className="sr-only">{label}</span>
    </span>
  )

  if (!tooltip) return badge

  return (
    <Tooltip
      inline
      placement="top-arrow"
      maxWidth={240}
      wrapperClassName={cn('flex shrink-0', className)}
      label={tooltip}
    >
      {badge}
    </Tooltip>
  )
}

export default NavBadge
